import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All possible deal statuses
export const DEAL_STATUSES = [
  'Funded',
  'Payment Issues',
  'In-House Collection',
  'Default',
  'Collected',
  'Paid in Full',
  'Paid in Full (Refinanced)',
  'Paid in Full / Settled',
  'Cancelled'
];

export const getStatusBadgeColor = (status) => {
  const colors = {
    'Funded': 'bg-green-100 text-green-800',
    'Payment Issues': 'bg-yellow-100 text-yellow-800',
    'In-House Collection': 'bg-orange-100 text-orange-800',
    'Default': 'bg-red-100 text-red-800',
    'Collected': 'bg-blue-100 text-blue-800',
    'Paid in Full': 'bg-purple-100 text-purple-800',
    'Paid in Full (Refinanced)': 'bg-indigo-100 text-indigo-800',
    'Paid in Full / Settled': 'bg-teal-100 text-teal-800',
    'Cancelled': 'bg-gray-200 text-gray-500'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Custom hook for fetching and managing deals
 */
export const useDeals = (getAuthHeaders, queryParams = {}) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize queryParams so the effect only re-runs when the actual values change
  const queryKey = JSON.stringify(queryParams);

  const fetchDeals = useCallback(async (params = {}) => {
    setError(null);
    try {
      const mergedParams = { ...JSON.parse(queryKey), ...params };
      const searchParams = new URLSearchParams({ limit: '1000' });
      
      // Add any additional params
      Object.entries(mergedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value);
        }
      });
      
      const response = await axios.get(`${API}/deals?${searchParams.toString()}`, { headers: getAuthHeaders() });
      setDeals(response.data);
    } catch (err) {
      console.error('Failed to fetch deals:', err);
      setError(err.message || 'Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, queryKey]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  return {
    deals,
    setDeals,
    loading,
    error,
    refetch: fetchDeals,
  };
};

/**
 * Custom hook for fetching contacts (brokers, merchants, funders)
 */
export const useContacts = (getAuthHeaders) => {
  const [contacts, setContacts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [funders, setFunders] = useState([]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
      const allContacts = response.data;
      setContacts(allContacts);
      setBrokers(allContacts.filter(c => c.is_broker || c.is_iso || (c.contact_types || []).some(t => ['Broker', 'ISO'].includes(t))));
      setMerchants(allContacts.filter(c => c.is_merchant || (c.contact_types || []).includes('Merchant')));
      setFunders(allContacts.filter(c => c.is_funder || (c.contact_types || []).includes('Funder')));
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  return {
    contacts,
    brokers,
    merchants,
    funders,
    refetchContacts: fetchContacts,
  };
};

/**
 * Custom hook for filtering deals by status and search query
 */
export const useDealsFilter = (deals) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);

  const filteredDeals = useMemo(() => {
    let filtered = [...deals];

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(deal => statusFilter.includes(deal.status));
    }

    // Apply search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(deal => 
        deal.deal_name?.toLowerCase().includes(lowerQuery) ||
        deal.deal_id?.toString().includes(lowerQuery) ||
        deal.deal_shortcode?.toLowerCase().includes(lowerQuery) ||
        deal.company_name?.toLowerCase().includes(lowerQuery)
      );
    }

    return filtered;
  }, [deals, searchQuery, statusFilter]);

  const toggleStatusFilter = useCallback((status) => {
    setStatusFilter(prev => 
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  }, []);

  const clearStatusFilter = useCallback(() => {
    setStatusFilter([]);
  }, []);

  const activeFilterCount = statusFilter.length;

  return {
    filteredDeals,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    toggleStatusFilter,
    clearStatusFilter,
    activeFilterCount,
  };
};

/**
 * Custom hook for deal selection management
 */
export const useDealsSelection = () => {
  const [selectedDeals, setSelectedDeals] = useState([]);

  const selectDeal = useCallback((dealId) => {
    setSelectedDeals(prev => 
      prev.includes(dealId)
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  }, []);

  const selectAll = useCallback((deals) => {
    setSelectedDeals(prev => 
      prev.length === deals.length ? [] : deals.map(d => d.id)
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedDeals([]);
  }, []);

  return {
    selectedDeals,
    setSelectedDeals,
    selectDeal,
    selectAll,
    clearSelection,
  };
};

/**
 * Custom hook for deal CRUD operations
 */
export const useDealsActions = (getAuthHeaders, refetchDeals) => {
  const createDeal = useCallback(async (dealData) => {
    try {
      const response = await axios.post(`${API}/deals`, dealData, { headers: getAuthHeaders() });
      toast.success('Deal created successfully');
      refetchDeals();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create deal';
      toast.error(errorMessage);
      throw error;
    }
  }, [getAuthHeaders, refetchDeals]);

  const updateDeal = useCallback(async (dealId, dealData) => {
    try {
      const response = await axios.put(`${API}/deals/${dealId}`, dealData, { headers: getAuthHeaders() });
      toast.success('Deal updated successfully');
      refetchDeals();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deal');
      throw error;
    }
  }, [getAuthHeaders, refetchDeals]);

  const deleteDeal = useCallback(async (dealId) => {
    try {
      await axios.delete(`${API}/deals/${dealId}`, { headers: getAuthHeaders() });
      toast.success('Deal deleted successfully');
      refetchDeals();
      return true;
    } catch (error) {
      toast.error('Failed to delete deal');
      return false;
    }
  }, [getAuthHeaders, refetchDeals]);

  const bulkDeleteDeals = useCallback(async (dealIds) => {
    try {
      await axios.post(`${API}/deals/bulk-delete`, dealIds, { headers: getAuthHeaders() });
      toast.success(`Deleted ${dealIds.length} deal(s) successfully`);
      refetchDeals();
      return true;
    } catch (error) {
      toast.error('Failed to delete deals');
      return false;
    }
  }, [getAuthHeaders, refetchDeals]);

  return {
    createDeal,
    updateDeal,
    deleteDeal,
    bulkDeleteDeals,
  };
};

export default useDeals;

// Re-export additional hooks for convenience
export { useRefinanceMode } from './useRefinanceMode';
export { useDealFormCalculations, addBusinessDays } from './useDealFormCalculations';
